import type { TTSChunk } from "./types";

const enc = new TextEncoder();

/**
 * SentenceTokenizer — the SINGLE authority for where a spoken chunk begins and
 * ends. There is no second opinion anywhere in the system: a chunk either has
 * been emitted or it has not. This is what makes the old "guard vs. flusher
 * disagreement" freeze structurally impossible.
 *
 * One tokenizer per turn (capture). Text is fed in reading order via `push`,
 * regardless of source — streaming deltas across several entries (split by tool
 * calls) or a completed block pushed whole. The tokenizer accumulates until it
 * passes a sentence-ending punctuation, then emits everything up to and
 * including it as one chunk. On `end()` the trailing remainder is flushed.
 *
 * Emission rule (the ONLY rule):
 *   - `.` `?` `;` followed by whitespace/newline is a confirmed boundary.
 *   - a newline is always a boundary.
 *   - a bare `.` with nothing after it yet is HELD (e.g. "v2.0", "3.5", or a
 *     sentence end whose trailing space hasn't streamed in) — never emitted
 *     mid-buffer.
 *   - `end()` emits whatever remains as the final chunk.
 *
 * Voice and the block-root entry id are stamped onto every chunk so the player
 * is a dumb consumer: it never tracks "which turn am I in" — each chunk is a
 * self-contained unit of work (text + voice + routing).
 */
export class SentenceTokenizer {
  private readonly voice: string;
  private readonly rootEntryId: string;
  private readonly emit: (chunk: TTSChunk) => void;

  // The unit (content item) currently being accumulated. A sentence never
  // spans two units (separate text blocks / tool-call entries), so when the
  // unit changes we force-close the held remainder first.
  private curKey: string | null = null;
  private curEntryId: string | null = null;
  private buffer = "";        // unemitted text for the current unit
  private emittedBytes = 0;   // UTF-8 bytes already emitted for the current unit

  constructor(voice: string, rootEntryId: string, emit: (chunk: TTSChunk) => void) {
    this.voice = voice;
    this.rootEntryId = rootEntryId;
    this.emit = emit;
  }

  /**
   * Append text for a unit `(entryId, key)` in reading order. Emits every
   * confirmed sentence. `suffix` is only the NEW text since the last push for
   * this unit (the capture tracks how much it has already handed over).
   */
  push(entryId: string, key: string, suffix: string): void {
    if (!suffix) return;
    // Normalize all newline styles to "\n" so a single boundary rule covers
    // "\n", "\r\n", and "\r". Stripping the "\r" also keeps it out of emitted
    // chunk text and byte offsets.
    suffix = suffix.replace(/\r\n?/g, "\n");
    if (key !== this.curKey) {
      // A new unit begins — force-close the previous unit's held remainder so
      // a trailing partial (e.g. text right before a tool call) is still spoken.
      this.flushRemainder();
      this.curKey = key;
      this.curEntryId = entryId;
      this.buffer = "";
      this.emittedBytes = 0;
    }
    this.buffer += suffix;
    this.scan();
  }

  /**
   * Force-close a specific unit's held remainder NOW (used when a content item
   * is finalized, e.g. text right before a tool call, which otherwise has no
   * trailing boundary and would sit held until the next unit begins). Only
   * flushes if that unit is the one currently buffered.
   */
  flushUnit(key: string): void {
    if (key === this.curKey) this.flushRemainder();
  }

  /**
   * Force-close whatever is currently buffered NOW, regardless of unit. Used
   * when an out-of-band signal proves the current text is complete — e.g. a
   * tool call starts (a tool_use content or a new child entry appears). The
   * text preceding it is done; don't wait for the next text unit to arrive.
   */
  flushCurrent(): void {
    this.flushRemainder();
  }

  /** No more text will arrive: flush the trailing remainder as the final chunk. */
  end(): void {
    this.flushRemainder();
  }

  // Emit every confirmed sentence at the front of the buffer.
  private scan(): void {
    const re = /([.?;][ \t]+|[.?;]\n|\n)/g;
    let cut = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(this.buffer)) !== null) {
      const end = m.index + m[0].length;
      this.emitChunk(this.buffer.slice(cut, end));
      cut = end;
    }
    if (cut > 0) this.buffer = this.buffer.slice(cut);
  }

  private flushRemainder(): void {
    if (this.buffer.length > 0 && this.curEntryId) {
      this.emitChunk(this.buffer);
      this.buffer = "";
    }
  }

  private emitChunk(text: string): void {
    if (!text) return;
    this.emit({
      text,
      voice: this.voice,
      entryId: this.curEntryId!,
      unitKey: this.curKey!,
      rootEntryId: this.rootEntryId,
      baseOffset: this.emittedBytes,
    });
    this.emittedBytes += enc.encode(text).length;
  }
}
